import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useIdentity } from "./hooks";
import { SignInPanel } from "./sign-in-panel";

/**
 * Renders the app only for a signed-in caller.
 *
 * This is **not** the security boundary — every catalog route is guarded
 * server-side in `routes/blocks.py`, and has to be, because nothing stops
 * anyone calling the API without this component in the way. What this buys is
 * that a signed-out visitor sees a sign-in screen rather than the gallery's
 * "could not load the registry" error, which looks like an outage.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { data, isPending, error } = useIdentity();

  // Every pre-authenticated state centres in the same box, so moving between
  // them (spinner -> sign-in, sign-in -> error) does not shift the page.
  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-12 text-sm text-ink-400">
        <Loader2 className="animate-spin" size={16} />
        Checking your session…
      </div>
    );
  }

  if (error) {
    // 401 is "sign in"; anything else is the server being broken, and telling
    // someone to sign in when the API is down sends them round a loop they
    // cannot get out of.
    if (error instanceof ApiError && error.status === 401) return <SignInPanel />;
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <p className="max-w-sm rounded-lg border border-ink-700 bg-ink-900 px-4 py-3 text-center text-sm">
          Could not check your session: {error.message}
        </p>
      </div>
    );
  }

  return data ? <>{children}</> : <SignInPanel />;
}
